/**
 * DEFINE — workflows (the front door), controls, and policies.
 *
 * These are the setup surfaces. They are visited once when a workspace is stood
 * up and then rarely, which is exactly why they need to be the most explicit
 * screens in the product: the person reading them is doing it for the first time.
 */

import { useMemo, useState } from "react";
import { intakeOracleIsIndependent } from "@excellent/core/verification-workflow-intake";
import {
  CANDIDATE_STATUS_LABEL,
  CANDIDATE_STATUS_TONE,
  HEALTH_TONE,
  POLICIES,
  actor,
  candidate as findCandidate,
  checksForControl,
  control as findControl,
  fmtDateTime,
  fmtNumber,
  fmtRelative,
  policy as findPolicy,
  requirementsFor,
  source,
  type Candidate,
  type Control,
  type Policy,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Choice,
  Code,
  Dialog,
  Empty,
  MissingObjectPage,
  Field,
  FieldGroup,
  Fields,
  Grid,
  Label,
  PageShell,
  Section,
  SearchBox,
  Select,
  Severity,
  Stat,
  Table,
  TextArea,
  TextInput,
  Verdict,
} from "./ui";
import type { Column } from "./ui";
import { openFindings, useStore } from "./store";

/* ══════════════════════════════════════════════════════════════════════════
   WORKFLOWS — the front door
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Workflows.
 *
 * ── This page exists because of a specific audit finding ──
 *
 * The UX audit's top item was that the agent-hookup path "was under-documented"
 * and that users "had to infer the path from endpoint names and PRD notes", with
 * the recommendation being "a compact 'Connect an agent' affordance above the
 * decision queue". This page is the fuller answer: a first-class surface where
 * connecting a workflow is the primary action, and where the ONE rule that governs
 * admission is stated on screen rather than discovered by being refused.
 *
 * ── The rule is the design ──
 *
 * A workflow cannot be admitted without an independent oracle. The page teaches
 * that by showing a candidate that fails the test (`cnd_0073`, an LLM reviewing
 * its own work) next to ones that pass, with the refusal reason in full. Making
 * the counter-example visible is worth more than any amount of help text, because
 * the mistake it prevents — asking an agent to confirm its own success — is the
 * single most common way people try to use a system like this.
 */
export function WorkflowsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [composing, setComposing] = useState(false);

  const admitted = store.candidates.filter((c) => c.status === "admitted");
  const pending = store.candidates.filter((c) => c.status === "needs-oracle" || c.status === "draft");
  const rejected = store.candidates.filter((c) => c.status === "reject");

  return (
    <PageShell
      eyebrow="Define"
        title="Workflows"
        lede="A workflow is any real thing — done by a person or an agent — whose success you do not want to take on trust. Connecting one means naming what must be true and the independent source that can confirm it."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setComposing(true)}>
            Connect a workflow
          </Button>
        }
    >

      <Callout tone="accent" icon="shield" title="One rule governs everything here.">
        {/* This sentence states a RULE, so the page applies the engine's rule rather
            than a fixture's opinion of it. `oracleIsIndependent` still exists on the
            fixture, but nothing here reads it — a hand-set boolean duplicating a
            computable property is identical the day it is written and silent the day
            it stops being true. Measured before changing: all 5 candidates agreed. */}
        A workflow is admitted only if it names an <strong>independent external oracle</strong> — a source of
        truth the actor doing the work does not control. An agent summarising its own success is not evidence,
        and a receipt built on one asserts nothing. Candidates without an oracle stay in{" "}
        <span className="vp-mono">needs-oracle</span> indefinitely; they are not quietly downgraded and passed.
      </Callout>

      <Section title="Admitted" note={`${admitted.length} workflows are being verified.`}>
        <div className="vp-stack" data-gap="sm">
          {admitted.map((c) => (
            <CandidateCard key={c.id} candidate={c} onNavigate={onNavigate} />
          ))}
        </div>
      </Section>

      <Section
        title="Waiting on an oracle"
        note={`${pending.length} candidates cannot be admitted as written.`}
      >
        <div className="vp-stack" data-gap="sm">
          {pending.map((c) => (
            <CandidateCard key={c.id} candidate={c} onNavigate={onNavigate} />
          ))}
          {pending.length === 0 ? (
            <Card>
              <Empty title="Nothing waiting">Every proposed workflow has an independent oracle.</Empty>
            </Card>
          ) : null}
        </div>
      </Section>

      {rejected.length > 0 ? (
        <Section title="Not admitted" note="Kept on the record, with the reason, rather than deleted.">
          <div className="vp-stack" data-gap="sm">
            {rejected.map((c) => (
              <CandidateCard key={c.id} candidate={c} onNavigate={onNavigate} />
            ))}
          </div>
        </Section>
      ) : null}

      {composing ? <ConnectWorkflowDialog onClose={() => setComposing(false)} /> : null}
    </PageShell>
  );
}

function CandidateCard({
  candidate,
  onNavigate,
}: {
  candidate: Candidate;
  onNavigate: (to: string) => void;
}) {
  const store = useStore();
  return (
    <Card
      title={candidate.name}
      subtitle={
        <>
          <span className="vp-mono">{candidate.id}</span> · proposed by{" "}
          {actor(candidate.createdByActorId).name} {fmtRelative(candidate.createdAt)}
        </>
      }
      actions={
        <>
          <Severity level={candidate.consequence} />
          <Badge tone={CANDIDATE_STATUS_TONE[candidate.status]}>
            {CANDIDATE_STATUS_LABEL[candidate.status]}
          </Badge>
        </>
      }
      footer={
        <>
          <Button
            size="sm"
            variant="ghost"
            icon="arrow-right"
            onClick={() => onNavigate(href("workflows", candidate.id))}
          >
            Detail
          </Button>
          {candidate.status === "draft" || candidate.status === "needs-oracle" ? (
            <span style={{ marginLeft: "auto" }}>
              <Button
                size="sm"
                variant={intakeOracleIsIndependent(candidate.oracle) ? "primary" : "outline"}
                icon="check"
                onClick={() => store.admitCandidate(candidate.id)}
                title={
                  intakeOracleIsIndependent(candidate.oracle)
                    ? "Admit this workflow into verification"
                    : "Blocked — no independent oracle"
                }
              >
                Admit
              </Button>
            </span>
          ) : null}
        </>
      }
    >
      <Fields>
        <Field label="Who does it">{candidate.actor}</Field>
        <Field label="Who checks it">{candidate.verifier}</Field>
        <Field label="Independent oracle">
          {candidate.oracle ? (
            <span className="vp-inline">
              <Badge tone="pass" glyph="✓">
                present
              </Badge>
              <span>{candidate.oracle}</span>
            </span>
          ) : (
            <Badge tone="fail" glyph="✕">
              none named
            </Badge>
          )}
        </Field>
        <Field label="Evidence">{candidate.evidence}</Field>
      </Fields>

      {candidate.rejectionReason ? (
        <div style={{ marginTop: "calc(var(--spacing) * 3)" }}>
          <Callout tone={candidate.status === "needs-oracle" ? "fail" : "neutral"} icon="alert" title="Why this is not admitted">
            {candidate.rejectionReason}
          </Callout>
        </div>
      ) : null}
    </Card>
  );
}

export function WorkflowDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const c = store.candidates.find((x) => x.id === id) ?? findCandidate(id);

  if (!c) {
    return (
      <MissingObjectPage title={`No workflow ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("workflows"))}>
          Back to workflows
        </Button>
      </MissingObjectPage>
    );
  }

  return (
    <PageShell
      eyebrow={`Workflow · ${CANDIDATE_STATUS_LABEL[c.status]}`}
        title={c.name}
        lede={`Consequence ${c.consequence}. Proposed by ${actor(c.createdByActorId).name} on ${fmtDateTime(c.createdAt)}.`}
        actions={
          c.status === "admitted" ? (
        <Button
              variant="primary"
              icon="shield"
              onClick={() => onNavigate(href("controls", c.admittedControlId ?? "ctl_cc81"))}
            >
              Open the control
        </Button>
          ) : (
            // ENABLED even when the oracle test fails, matching the list card.
            // The store refuses and says why, which teaches the rule; a disabled
            // button teaches nothing and leaves you guessing which of the four
            // answers was wrong. The two surfaces used to disagree about this.
        <Button variant="primary" icon="check" onClick={() => store.admitCandidate(c.id)}>
              Admit
        </Button>
          )
        }
    >

      <div className="vp-split">
        <div className="vp-stack">
          <Card title="The four questions every workflow must answer">
            <Fields>
              <Field label="1. What happens">{c.name}</Field>
              <Field label="2. Who does it">{c.actor}</Field>
              <Field label="3. Who checks it">{c.verifier}</Field>
              <Field label="4. What proves it">{c.evidence}</Field>
            </Fields>
          </Card>

          <Card title="The oracle test">
            {c.oracle && intakeOracleIsIndependent(c.oracle) ? (
              <Callout tone="pass" icon="check" title="Passes: the oracle is independent of the actor.">
                <p style={{ margin: "0 0 calc(var(--spacing) * 1.5)" }}>{c.oracle}</p>
                The actor cannot alter this source to make itself look successful, which is what makes a verdict
                on it worth signing.
              </Callout>
            ) : (
              <Callout tone="fail" icon="x" title="Fails: no source independent of the actor.">
                <p style={{ margin: "0 0 calc(var(--spacing) * 1.5)" }}>{c.rejectionReason}</p>
                <strong>To fix it:</strong> name something the actor does not write. A test suite result, an
                approving reviewer's identity, a payment processor's state, a post-deployment error rate — any
                of these can settle the question without asking the actor to grade itself.
              </Callout>
            )}
          </Card>

          <Card
            title="How your agent hooks in"
            subtitle={
              intakeOracleIsIndependent(c.oracle)
                ? "Once admitted, this is the integration."
                : "What the integration would look like — once this workflow has an oracle it can be admitted with."
            }
          >
            {/* The sample is built from THIS candidate rather than being a fixed
                refund example, so it never shows a payment payload on a
                code-review workflow. */}
            <Code>{`POST /api/verification/v1/claims
{
  "workflow": "${c.id}",
  "claim": "${c.name.toLowerCase()} — for <your job id>",
  "evidence": [
${c.evidence
  .split(",")
  .map((e) => `    { "kind": "${e.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_")}", "ref": "<ref>" }`)
  .join(",\n")}
  ]
}

-> 201 {
     "runId": "run_8819",
     "route": "/verification/runs/run_8819",
     "bundleHash": "sha256:...",
     "verdict": "PENDING"
   }`}</Code>
            <p className="vp-help">
              Store the <span className="vp-mono">runId</span>, <span className="vp-mono">route</span> and{" "}
              <span className="vp-mono">bundleHash</span> on your own job record. That is what lets your system
              point at proof later without having to trust its own logs.
            </p>
          </Card>
        </div>

        <aside className="vp-split-aside">
          <Card title="Record">
            <Fields>
              <Field label="Candidate id">
                <span className="vp-mono">{c.id}</span>
              </Field>
              <Field label="Status">
                <Badge tone={CANDIDATE_STATUS_TONE[c.status]} size="lg">
                  {CANDIDATE_STATUS_LABEL[c.status]}
                </Badge>
              </Field>
              <Field label="Consequence">
                <Severity level={c.consequence} />
              </Field>
              <Field label="Proposed">{fmtDateTime(c.createdAt)}</Field>
              {c.admittedControlId ? (
                <Field label="Control">
                  <span className="vp-mono">{findControl(c.admittedControlId)?.ref}</span>
                </Field>
              ) : null}
            </Fields>
          </Card>

          <Card title="Why the bar is here">
            <p style={{ margin: "0", fontSize: "var(--type-subtext-size)", lineHeight: "var(--type-subtext-leading)", color: "var(--ink-2)" }}>
              Every other guarantee in this product rests on this one. If a workflow is admitted without an
              independent oracle, the checks run against evidence the actor controls, the verdict is
              circular, and the receipt — cryptographically perfect, offline-verifiable, signed with your own
              key — attests to nothing. The signature would be real and the claim would be empty.
            </p>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}

/**
 * The connect dialog.
 *
 * A four-question form rather than a JSON editor, and the oracle question carries
 * a live verdict: pick "the agent's own report" and the dialog refuses before you
 * submit, naming the reason. Teaching the rule at the point of the mistake is the
 * whole purpose of the interaction.
 */
function ConnectWorkflowDialog({ onClose }: { onClose: () => void }) {
  const store = useStore();
  const [name, setName] = useState("");
  const [who, setWho] = useState("");
  const [oracleKind, setOracleKind] = useState<"external" | "self" | null>(null);
  const [oracle, setOracle] = useState("");

  const independent = oracleKind === "external";
  const canSubmit = name.trim() && who.trim() && independent && oracle.trim();

  return (
    <Dialog
      title="Connect a workflow"
      subtitle="Four questions. The third is the one that decides whether this can be verified at all."
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={!canSubmit}
            onClick={() => {
              store.toast(
                "Prototype: the candidate would be created and admitted, then verified on the next run.",
              );
              onClose();
            }}
          >
            Create and admit
          </Button>
        </>
      }
    >
      <div className="vp-form">
        <div>
          <Label required htmlFor="wf-name">
            What happens that needs verifying?
          </Label>
          <TextInput
            id="wf-name"
            value={name}
            onChange={setName}
            placeholder="e.g. A refund above $5,000 was actually issued"
          />
          <p className="vp-help">One concrete claim, phrased so it can be true or false.</p>
        </div>

        <div>
          <Label required htmlFor="wf-actor">
            Who or what does it?
          </Label>
          <TextInput
            id="wf-actor"
            value={who}
            onChange={setWho}
            placeholder="e.g. billing-agent (automated)"
          />
        </div>

        <div>
          <FieldGroup label="What can confirm it, independently?" required id="wf-oracle">
            <Choice
              selected={oracleKind === "external"}
              title="An external system the actor does not control"
              desc="A payment processor's state, an identity provider's log, a test suite result, an approving reviewer's identity. This is the only answer that can be admitted."
              onSelect={() => setOracleKind("external")}
            />
            <Choice
              selected={oracleKind === "self"}
              title="The actor's own report of what it did"
              desc="An agent's summary, a self-reported status, a log the actor writes. This cannot be admitted."
              onSelect={() => setOracleKind("self")}
            />
          </FieldGroup>

          {oracleKind === "self" ? (
            <div style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
              <Callout tone="fail" icon="x" title="This cannot be verified.">
                You would be asking the thing being checked to confirm itself. The workflow stays in{" "}
                <span className="vp-mono">needs-oracle</span> until an independent source is named — it is not
                admitted with a warning.
              </Callout>
            </div>
          ) : null}

          {oracleKind === "external" ? (
            <div style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
              <Label htmlFor="wf-oracle-detail">Name the oracle</Label>
              <TextInput
                id="wf-oracle-detail"
                value={oracle}
                onChange={setOracle}
                placeholder='e.g. Stripe API — refund.status == "succeeded" and amount matches the approved figure'
              />
              <p className="vp-help">
                Name the system and the specific condition. "Stripe" is a system; "refund.status ==
                succeeded" is a check.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CONTROLS
   ══════════════════════════════════════════════════════════════════════════ */

export function ControlsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");

  const families = useMemo(
    () => Array.from(new Set(store.controls.map((c) => c.family))).sort(),
    [store.controls],
  );

  const rows = useMemo(() => {
    let list = store.controls;
    if (family !== "all") list = list.filter((c) => c.family === family);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (c) => c.title.toLowerCase().includes(n) || c.ref.toLowerCase().includes(n) || c.family.toLowerCase().includes(n),
      );
    }
    return list;
  }, [store.controls, family, q]);

  const columns: Column<Control>[] = [
    {
      key: "ref",
      header: "Control",
      primary: true,
      sortValue: (c) => c.ref,
      render: (c) => (
        <>
          <span className="vp-mono" style={{ color: "var(--ink-3)" }}>
            {c.ref}
          </span>
          <div style={{ marginTop: "calc(var(--spacing) * 0.5)" }}>{c.title}</div>
        </>
      ),
    },
    // No "Family" column: the filter bar above already scopes by family, so the
    // column repeated the filter while costing the width that pushed "Owner" and
    // "Frequency" off a laptop viewport.
    {
      key: "verdict",
      header: "Verdict",
      sortValue: (c) => c.status,
      render: (c) => <Verdict status={c.status} />,
    },
    {
      key: "sev",
      header: "Consequence",
      sortValue: (c) => ({ critical: 0, high: 1, medium: 2, low: 3 })[c.consequence],
      render: (c) => <Severity level={c.consequence} />,
    },
    {
      key: "open",
      header: "Open findings",
      sortValue: (c) => openFindings(store.findings).filter((f) => f.controlId === c.id).length,
      render: (c) => {
        const n = openFindings(store.findings).filter((f) => f.controlId === c.id).length;
        return n === 0 ? <span className="vp-muted">—</span> : <Badge tone="fail">{n}</Badge>;
      },
    },
    {
      key: "pop",
      header: "Population",
      sortValue: (c) => c.populationSize,
      render: (c) => (
        <span className="vp-mono">
          {c.sampledSize === c.populationSize
            ? `${fmtNumber(c.populationSize)} (all)`
            : `${fmtNumber(c.sampledSize)} / ${fmtNumber(c.populationSize)}`}
        </span>
      ),
    },
    { key: "owner", header: "Owner", sortValue: (c) => actor(c.ownerActorId).name, render: (c) => actor(c.ownerActorId).name },
    { key: "freq", header: "Frequency", sortValue: (c) => c.frequency, render: (c) => c.frequency },
  ];

  return (
    <PageShell
      wide
      eyebrow="Define"
        title="Controls"
        lede="What must be true, who owns it, how often it is checked, and how much of the population the check actually covers."
        actions={
          <Button
            variant="primary"
            icon="refresh"
            onClick={() => store.toast("Full control sweep queued across 8 controls.")}
          >
            Run all
          </Button>
        }
    >

      <div className="vp-filters">
        <SearchBox value={q} onChange={setQ} placeholder="Search controls…" />
        <Select
          label="Family"
          value={family}
          onChange={setFamily}
          options={[{ value: "all", label: "All families" }, ...families.map((f) => ({ value: f, label: f }))]}
        />
        <span className="vp-filter-spacer" />
        <span className="vp-result-count">
          {rows.length} of {store.controls.length}
        </span>
      </div>

      <Card flush>
        <Table
          columns={columns}
          rows={rows}
          rowKey={(c) => c.id}
          onRowClick={(c) => onNavigate(href("controls", c.id))}
          empty={<Empty title="No controls match">Clear the filters to see the full library.</Empty>}
        />
      </Card>

      <Callout tone="neutral" icon="info">
        <strong>Sampling is stated, never implied.</strong> Three of these controls do not cover their full
        population — two sample, and one has never run — and the pack says so. A control tested on 25 of 68 grants is not the same claim
        as one tested on all of them, and collapsing that difference is how evidence packages become
        misleading.
      </Callout>
    </PageShell>
  );
}

export function ControlDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const c = store.controls.find((x) => x.id === id) ?? findControl(id);

  if (!c) {
    return (
      <MissingObjectPage title={`No control ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("controls"))}>
          Back to controls
        </Button>
      </MissingObjectPage>
    );
  }

  const reqs = requirementsFor(c.id);
  const checks = checksForControl(c.id);
  const open = openFindings(store.findings).filter((f) => f.controlId === c.id);
  // Bound rather than indexed inline — see the same note in `pages-overview.tsx`.
  const [firstOpen] = open;
  const pol = POLICIES.filter((p) => p.controlId === c.id);
  const active = pol.find((p) => p.status === "active");
  const srcs = c.sourceIds.map((sid) => source(sid)).filter(Boolean);

  return (
    <PageShell
      wide
      eyebrow={`${c.framework} ${c.ref} · ${c.family}`}
        title={c.title}
        lede={`Owned by ${actor(c.ownerActorId).name}. Checked ${c.frequency}. Consequence ${c.consequence}.`}
        actions={
          <>
        <Button icon="refresh" onClick={() => store.reverify(c.id)}>
              Re-verify
        </Button>
            {active ? (
              <Button variant="primary" icon="file" onClick={() => onNavigate(href("policies", active.id))}>
                Open policy
              </Button>
            ) : null}
          </>
        }
    >

      <Grid cols={4}>
        <Stat label="Current verdict" value={<Verdict status={c.status} size="lg" />} />
        <Stat
          label="Open findings"
          value={open.length}
          tone={open.length > 0 ? "fail" : "pass"}
          foot={open.length > 0 ? "Needs a decision" : "Nothing outstanding"}
          onClick={firstOpen ? () => onNavigate(href("findings", firstOpen.id)) : undefined}
        />
        <Stat
          label="Population tested"
          value={`${Math.round((c.sampledSize / (c.populationSize || 1)) * 100)}%`}
          tone={c.sampledSize === c.populationSize ? "pass" : "unknown"}
          foot={`${fmtNumber(c.sampledSize)} of ${fmtNumber(c.populationSize)} items`}
        />
        <Stat
          label="Evidence sources"
          value={srcs.length}
          tone={srcs.length === 0 ? "fail" : srcs.some((s) => s!.health !== "healthy") ? "unknown" : "pass"}
          foot={srcs.length === 0 ? "Nothing mapped — the control cannot run" : srcs.map((s) => s!.provider).join(", ")}
          onClick={() => onNavigate(href("connectors"))}
        />
      </Grid>

      {c.status === "NOT_RUN" ? (
        <div style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
          <Callout tone="unknown" icon="alert" title="This control has never been verified.">
            No evidence source is mapped to it, so no check has ever run. It will appear in any pack as a
            scope gap — which is honest, but it is not evidence. Map a source or import the records manually to
            change that.
          </Callout>
        </div>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5.5)" }}>
        <div className="vp-stack">
          <Card
            title="Requirements"
            subtitle="What the control must demonstrate. Each one is checked separately."
            flush
          >
            <div className="vp-rows">
              {reqs.map((r) => {
                const k = checks.find((x) => x.requirementId === r.id);
                return (
                  <div key={r.id} className="vp-row" style={{ alignItems: "flex-start" }}>
                    <span style={{ flex: "0 0 auto", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                      {k ? <Verdict status={k.status} /> : <Verdict status="NOT_RUN" />}
                    </span>
                    <span className="vp-row-main">
                      <span className="vp-row-title">{r.text}</span>
                      <span className="vp-row-sub">
                        Evidence kinds:{" "}
                        {r.evidenceKinds.map((e) => (
                          <span key={e} className="vp-mono" style={{ marginRight: "calc(var(--spacing) * 1.5)" }}>
                            {e}
                          </span>
                        ))}
                      </span>
                      {k ? <span className="vp-row-sub">{k.reason}</span> : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {active ? (
            <Card
              title="Active policy"
              subtitle={`${active.id} · version ${active.version} · activated ${fmtDateTime(active.activatedAt ?? "")} by ${actor(active.activatedByActorId ?? "").name}`}
              actions={
                <Button size="sm" variant="ghost" icon="arrow-right" onClick={() => onNavigate(href("policies", active.id))}>
                  Open
                </Button>
              }
            >
              <Code>{active.expression}</Code>
              <p className="vp-help">
                Evaluation mode <span className="vp-mono">{active.evaluationMode}</span> ·{" "}
                {active.frozenCasesPassing} of {active.frozenCaseCount} frozen regression cases passing.
              </p>
            </Card>
          ) : null}

          {open.length > 0 ? (
            <Card title="Open findings" flush>
              <div className="vp-rows">
                {open.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("findings", f.id))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">{f.title}</span>
                      <span className="vp-row-sub">
                        <span className="vp-mono">{f.ref}</span> · opened {fmtRelative(f.openedAt)}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Severity level={f.severity} />
                      <Verdict status={f.verificationStatus} />
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        <aside className="vp-split-aside">
          <Card title="Control record">
            <Fields>
              <Field label="Reference">
                <span className="vp-mono">{c.ref}</span>
              </Field>
              <Field label="Framework">{c.framework}</Field>
              <Field label="Family">{c.family}</Field>
              <Field label="Owner">{actor(c.ownerActorId).name}</Field>
              <Field label="Consequence">
                <Severity level={c.consequence} />
              </Field>
              <Field label="Frequency">{c.frequency}</Field>
              <Field label="Population">{fmtNumber(c.populationSize)}</Field>
              <Field label="Tested">
                {c.sampledSize === c.populationSize ? "Full population" : `Sample of ${fmtNumber(c.sampledSize)}`}
              </Field>
            </Fields>
          </Card>

          <Card title="Evidence sources" flush>
            {srcs.length === 0 ? (
              <div className="vp-card-body">
                <p className="vp-tiny" style={{ margin: "0" }}>
                  None mapped. This is why the control reads NOT RUN.
                </p>
              </div>
            ) : (
              <div className="vp-rows">
                {srcs.map((s) => (
                  <button
                    key={s!.id}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("connectors"))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">{s!.displayName}</span>
                      <span className="vp-row-sub">
                        Last collected{" "}
                        {s!.lastSuccessfulRunAt ? fmtRelative(s!.lastSuccessfulRunAt) : "never"}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Badge tone={HEALTH_TONE[s!.health]}>
                        {s!.health}
                      </Badge>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {pol.length > 1 ? (
            <Card title="Policy history" flush>
              <div className="vp-rows">
                {pol.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("policies", p.id))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">Version {p.version}</span>
                      <span className="vp-row-sub">
                        {p.status} · {p.intent}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Badge tone={p.status === "active" ? "pass" : p.blockedReason ? "fail" : "neutral"}>
                        {p.status}
                      </Badge>
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}
        </aside>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   POLICIES
   ══════════════════════════════════════════════════════════════════════════ */

export function PoliciesPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const blocked = POLICIES.filter((p) => p.status === "draft" && p.blockedReason);

  return (
    <PageShell
      wide
      eyebrow="Define"
        title="Policies"
        lede="The checkable logic behind each control, versioned. Tightening the bar is routine; loosening it is not — that path requires impact analysis against frozen cases and activation by a second person."
    >

      {blocked.length > 0 ? (
        <Callout
          tone="unknown"
          icon="alert"
          title={`${blocked.length} draft cannot activate.`}
        >
          A draft that would make a previously-failing case pass is blocked until someone other than its author
          reviews the impact. This is the same "no self-approval" rule the decision inbox enforces, applied to
          the rules themselves — otherwise a failing control could be fixed by editing the test.
        </Callout>
      ) : null}

      <Card flush>
        <Table
          columns={[
            {
              key: "control",
              header: "Control",
              primary: true,
              sortValue: (p: Policy) => findControl(p.controlId)?.ref ?? "",
              render: (p: Policy) => (
                <>
                  <span className="vp-mono" style={{ color: "var(--ink-3)" }}>
                    {findControl(p.controlId)?.ref}
                  </span>
                  <div style={{ marginTop: "calc(var(--spacing) * 0.5)" }}>{findControl(p.controlId)?.title}</div>
                </>
              ),
            },
            {
              key: "version",
              header: "Version",
              sortValue: (p: Policy) => p.version,
              render: (p: Policy) => <span className="vp-mono">v{p.version}</span>,
            },
            {
              key: "status",
              header: "Status",
              sortValue: (p: Policy) => p.status,
              render: (p: Policy) => (
                <Badge tone={p.status === "active" ? "pass" : p.blockedReason ? "fail" : "neutral"}>
                  {p.status}
                </Badge>
              ),
            },
            {
              key: "intent",
              header: "Change intent",
              sortValue: (p: Policy) => p.intent,
              render: (p: Policy) => (
                <Badge tone={p.intent === "loosen" ? "unknown" : p.intent === "tighten" ? "pass" : "neutral"}>
                  {p.intent}
                </Badge>
              ),
            },
            {
              key: "frozen",
              header: "Frozen cases",
              sortValue: (p: Policy) => p.frozenCasesPassing / (p.frozenCaseCount || 1),
              render: (p: Policy) => (
                <span
                  className="vp-mono"
                  style={{ color: p.frozenCasesPassing < p.frozenCaseCount ? "var(--fail)" : undefined }}
                >
                  {p.frozenCasesPassing} / {p.frozenCaseCount}
                </span>
              ),
            },
            {
              key: "activated",
              header: "Activated",
              sortValue: (p: Policy) => (p.activatedAt ? new Date(p.activatedAt).getTime() : 0),
              render: (p: Policy) =>
                p.activatedAt ? (
                  <span className="vp-mono">{fmtRelative(p.activatedAt)}</span>
                ) : (
                  <span className="vp-muted">never</span>
                ),
            },
          ]}
          rows={POLICIES}
          rowKey={(p) => p.id}
          onRowClick={(p) => onNavigate(href("policies", p.id))}
        />
      </Card>
    </PageShell>
  );
}

export function PolicyDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  // Every hook runs before the not-found early return — a hook below it would
  // change this component's hook count between a real id and a bad one, which
  // React rejects on the transition.
  const [rationale, setRationale] = useState("");
  const p = findPolicy(id);

  if (!p) {
    return (
      <MissingObjectPage title={`No policy ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("policies"))}>
          Back to policies
        </Button>
      </MissingObjectPage>
    );
  }

  const c = findControl(p.controlId);
  const reqs = requirementsFor(p.controlId);

  return (
    <PageShell
      wide
      eyebrow={`Policy · ${c?.ref} · version ${p.version}`}
        title={c?.title ?? p.id}
        lede={
          p.status === "active"
            ? `Active since ${fmtDateTime(p.activatedAt ?? "")}, activated by ${actor(p.activatedByActorId ?? "").name}.`
            : `Draft created ${fmtDateTime(p.createdAt)} by ${actor(p.createdByActorId).name}.`
        }
        actions={
          <>
            <Badge tone={p.status === "active" ? "pass" : p.blockedReason ? "fail" : "neutral"} size="lg">
              {p.status}
            </Badge>
            <Badge tone={p.intent === "loosen" ? "unknown" : p.intent === "tighten" ? "pass" : "neutral"} size="lg">
              {p.intent}
            </Badge>
          </>
        }
    >

      {p.blockedReason ? (
        <Callout tone="fail" icon="lock" title="This draft cannot be activated.">
          <p style={{ margin: "0 0 calc(var(--spacing) * 2)" }}>{p.blockedReason}</p>
          <strong>Your role cannot clear this.</strong> You are signed in as a reviewer; activating a loosening
          change needs a workspace owner who is not the draft's author. That is deliberate — if the person who
          wrote a weaker rule could also turn it on, the rule would not be a constraint.
        </Callout>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <div className="vp-stack">
          <Card title="Policy expression" subtitle="Evaluated against the evidence, deterministically.">
            <Code>{p.expression}</Code>
          </Card>

          <Card
            title="Frozen regression cases"
            subtitle="A fixed set of historical situations with known-correct answers. A policy change that flips any of them is not a refinement — it is a behaviour change."
          >
            <div className="vp-inline" style={{ marginBottom: "calc(var(--spacing) * 3)" }}>
              <Badge tone={p.frozenCasesPassing === p.frozenCaseCount ? "pass" : "fail"} size="lg">
                {p.frozenCasesPassing} of {p.frozenCaseCount} passing
              </Badge>
              {p.frozenCasesPassing < p.frozenCaseCount ? (
                <span className="vp-tiny">
                  {p.frozenCaseCount - p.frozenCasesPassing} case
                  {p.frozenCaseCount - p.frozenCasesPassing === 1 ? "" : "s"} would change answer
                </span>
              ) : null}
            </div>
            {p.frozenCasesPassing < p.frozenCaseCount ? (
              <Callout tone="fail" icon="alert" title="The direction of the change matters.">
                These cases flip from <strong>FAIL to PASS</strong>. A change that turns a previously-failing
                case green is exactly what a control owner under audit pressure would want, and exactly what a
                verification system must refuse to do quietly.
              </Callout>
            ) : (
              <Callout tone="pass" icon="check">
                Every frozen case still returns the answer it is recorded as having. This version can activate
                on a tightening or neutral intent without a second reviewer.
              </Callout>
            )}
          </Card>

          {p.status === "draft" ? (
            <Card title="Request activation" subtitle="Routed to a workspace owner who is not you.">
              <div className="vp-form">
                <div>
                  <Label required>Why should this change be made?</Label>
                  <TextArea
                    value={rationale}
                    onChange={setRationale}
                    ariaLabel="Activation rationale"
                    placeholder="What is wrong with the current version, and why is this the right correction?"
                  />
                  <p className="vp-help">
                    Carried on the policy's audit record permanently, and shown to the owner who decides.
                  </p>
                </div>
                <div className="vp-btn-row">
                  <Button
                    variant="primary"
                    icon="arrow-right"
                    disabled={!rationale.trim()}
                    onClick={() => {
                      store.toast("Activation request sent to Jordan Lee (owner).");
                      setRationale("");
                    }}
                  >
                    Send for activation
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>

        <aside className="vp-split-aside">
          <Card title="Policy record">
            <Fields>
              <Field label="Policy id">
                <span className="vp-mono">{p.id}</span>
              </Field>
              <Field label="Version">
                <span className="vp-mono">v{p.version}</span>
              </Field>
              <Field label="Control">
                <button
                  type="button"
                  className="vp-crumb"
                  style={{ color: "var(--accent)" }}
                  onClick={() => onNavigate(href("controls", p.controlId))}
                >
                  {c?.ref}
                </button>
              </Field>
              <Field label="Evaluation">
                <span className="vp-mono">{p.evaluationMode}</span>
              </Field>
              <Field label="Author">{actor(p.createdByActorId).name}</Field>
              <Field label="Activated by">
                {p.activatedByActorId ? actor(p.activatedByActorId).name : "—"}
              </Field>
              <Field label="Created">{fmtDateTime(p.createdAt)}</Field>
            </Fields>
          </Card>

          <Card title="Required requirements" subtitle="All must pass under this version.">
            <div className="vp-stack" data-gap="sm">
              {reqs.map((r) => (
                <div key={r.id} className="vp-inline" style={{ alignItems: "flex-start" }}>
                  <span style={{ flex: "0 0 auto", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                    {p.requiredRequirementIds.includes(r.id) ? (
                      <Badge tone="accent" glyph="✓">
                        required
                      </Badge>
                    ) : (
                      <Badge tone="neutral" glyph="–">
                        not required
                      </Badge>
                    )}
                  </span>
                  <span style={{ fontSize: "var(--type-subtext-size)", color: "var(--ink-2)", lineHeight: "var(--type-subtext-leading)", flex: 1 }}>
                    {r.text}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}
